import React from 'react';
import { Question } from '@/types';
import { scaleFontSize } from '@/utils/scaleFontSize';

interface UnifiedQuestionDisplayProps {
  question: Question;
  currentQuestionIndex: number;
  totalQuestions: number;
  timeRemaining: number;
  isTeacher?: boolean;
  showCorrectAnswer?: boolean;
  onAdvanceEarly?: () => void;
  sessionId?: string;
  playerId?: string;
  fontScale?: number;
}

const UnifiedQuestionDisplay: React.FC<UnifiedQuestionDisplayProps> = ({
  question,
  currentQuestionIndex,
  totalQuestions,
  timeRemaining,
  isTeacher = false,
  showCorrectAnswer = false,
  onAdvanceEarly,
  fontScale = 1,
}) => {
  const scaledFontSize = (value: string) => scaleFontSize(value, fontScale);

  // Colors matching the GameArena answer zones (top to bottom)
  const answerColors = [
    { bg: '#F44336', hover: '#D32F2F', text: '#FFFFFF' }, // Red (matches GameArena zone 0)
    { bg: '#2196F3', hover: '#1976D2', text: '#FFFFFF' }, // Blue (matches GameArena zone 1)
    { bg: '#4CAF50', hover: '#388E3C', text: '#FFFFFF' }, // Green (matches GameArena zone 2)
    { bg: '#FFA726', hover: '#FB8C00', text: '#FFFFFF' }, // Orange (matches GameArena zone 3)
  ];

  const getTimerColor = () => {
    if (timeRemaining <= 3) return '#E53E3E'; // Red for urgency
    if (timeRemaining <= 5) return '#D69E2E'; // Orange for warning
    return '#38A169'; // Green for normal
  };

  const getPhaseMessage = () => {
    if (timeRemaining > 5) {
      return 'Read and understand the question';
    } else if (timeRemaining > 3) {
      return 'Get ready to run!';
    } else {
      return 'RUN TO YOUR ANSWER!';
    }
  };

  return (
    <div className="uqd-root" style={{
      background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 25%, #059669 75%, #047857 100%)',
      minHeight: '100vh',
      padding: '1rem 1.25rem 1.5rem',
      color: 'white',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated background elements */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 20%, rgba(255,255,255,0.1) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%),
          radial-gradient(circle at 40% 90%, rgba(255,255,255,0.05) 0%, transparent 50%)
        `,
        pointerEvents: 'none'
      }} />

      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Header with progress and timer */}
        <div className="uqd-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.25rem',
          backgroundColor: 'rgba(15, 23, 42, 0.4)',
          padding: '0.85rem 1rem',
          borderRadius: '14px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.2)'
        }}>
          <div className="uqd-progress" style={{
            fontSize: scaledFontSize('0.95rem'),
            fontWeight: 'bold',
            color: 'rgba(226, 232, 240, 0.85)'
          }}>
            Question {currentQuestionIndex + 1} of {totalQuestions}
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.85rem'
          }}>
            {/* Phase message */}
            <div className="uqd-phase" style={{
              fontSize: scaledFontSize('0.95rem'),
              fontWeight: '600',
              color: '#F8FAFC',
              backgroundColor: 'rgba(15,23,42,0.75)',
              textAlign: 'center',
              minWidth: '170px',
              padding: '0.55rem 0.8rem',
              borderRadius: '12px',
              border: `2px solid ${getTimerColor()}`,
              boxShadow: '0 6px 18px rgba(15,23,42,0.35)',
              textShadow: '0 2px 6px rgba(0,0,0,0.45)'
            }}>
              {getPhaseMessage()}
            </div>

            {/* Timer */}
            <div className="uqd-timer" style={{
              fontSize: scaledFontSize('2.2rem'),
              fontWeight: 'bold',
              color: '#F8FAFC',
              backgroundColor: 'rgba(15,23,42,0.85)',
              padding: '0.7rem 1rem',
              borderRadius: '12px',
              minWidth: '96px',
              textAlign: 'center',
              border: `3px solid ${getTimerColor()}`,
              boxShadow: timeRemaining <= 3 ? `0 0 20px ${getTimerColor()}` : '0 10px 28px rgba(15,23,42,0.45)',
              animation: timeRemaining <= 3 ? 'pulse 0.5s infinite' : 'none',
              textShadow: '0 2px 6px rgba(0,0,0,0.4)'
            }}>
              {timeRemaining}s
            </div>
          </div>
        </div>

        {/* Question content */}
        <div className="uqd-question-box" style={{
          backgroundColor: 'rgba(15, 23, 42, 0.35)',
          padding: '1.5rem 1.75rem',
          borderRadius: '18px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.2)',
          marginBottom: '1.5rem',
          textAlign: 'center',
          maxWidth: '760px',
          marginInline: 'auto'
        }}>
          <h1 className="uqd-question-text" style={{
            fontSize: scaledFontSize('clamp(1.05rem, 0.95rem + 0.9vw, 1.45rem)'),
            fontWeight: 'bold',
            margin: '0 auto 0.85rem',
            lineHeight: '1.35',
            color: '#F8FAFC',
            textShadow: '0 2px 8px rgba(15, 23, 42, 0.6)',
            maxWidth: '480px'
          }}>
            {question.text}
          </h1>

          {/* Answer choices - Vertical list matching GameArena circle order */}
          <div className="uqd-choices" style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.55rem',
            maxWidth: '560px',
            margin: '0 auto'
          }}>
            {question.choices.map((choice, index) => {
              const color = answerColors[index] || answerColors[0];
              const isCorrect = showCorrectAnswer && index === question.correctAnswerIndex;

              return (
                <div
                  key={index}
                  style={{
                    backgroundColor: isCorrect ? '#38A169' : color.bg,
                    color: color.text,
                    padding: '0.75rem 0.95rem',
                    borderRadius: '12px',
                    textAlign: 'left',
                    fontSize: scaledFontSize('1rem'),
                    fontWeight: 'bold',
                    border: isCorrect ? '4px solid #68D391' : `2px solid ${color.hover}`,
                    boxShadow: '0 5px 14px rgba(0,0,0,0.2)',
                    transform: 'translateY(0)',
                    transition: 'all 0.3s ease',
                    cursor: 'default',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.8rem'
                  }}
                  onMouseEnter={(e) => {
                    if (!isCorrect) {
                      e.currentTarget.style.backgroundColor = color.hover;
                      e.currentTarget.style.transform = 'translateX(4px)';
                      e.currentTarget.style.boxShadow = '0 10px 24px rgba(0,0,0,0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isCorrect) {
                      e.currentTarget.style.backgroundColor = color.bg;
                      e.currentTarget.style.transform = 'translateX(0)';
                      e.currentTarget.style.boxShadow = '0 5px 14px rgba(0,0,0,0.2)';
                    }
                  }}
                >
                  {/* Color indicator circle (matches arena) */}
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    backgroundColor: color.bg,
                    border: `2px solid ${color.hover}`,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: scaledFontSize('1.05rem'),
                    fontWeight: 'bold',
                    boxShadow: '0 3px 8px rgba(0,0,0,0.3)'
                  }}>
                    {String.fromCharCode(65 + index)}
                  </div>

                  {/* Choice text */}
                  <div style={{
                    flex: 1,
                    lineHeight: '1.25'
                  }}>
                    {choice}
                  </div>

                  {/* Correct answer indicator */}
                  {isCorrect && (
                    <div style={{
                      fontSize: scaledFontSize('1.45rem'),
                      color: '#68D391',
                      flexShrink: 0
                    }}>
                      ✓
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Teacher controls */}
        {isTeacher && onAdvanceEarly && (
          <div style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            zIndex: 1000
          }}>
            <button
              onClick={onAdvanceEarly}
              style={{
                backgroundColor: '#E53E3E',
                color: 'white',
                border: 'none',
                borderRadius: '50px',
                padding: '1rem 2rem',
                fontSize: scaledFontSize('1.1rem'),
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 8px 25px rgba(229,62,62,0.4)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.8rem'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#C53030';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 30px rgba(229,62,62,0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#E53E3E';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(229,62,62,0.4)';
              }}
            >
              <span style={{ fontSize: scaledFontSize('1.2rem') }}>⏭️</span>
              Start Arena Phase
            </button>
          </div>
        )}
      </div>

      {/* CSS animations + mobile overrides */}
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
          100% { opacity: 1; transform: scale(1); }
        }

        /* Mobile: match ScoreScreen compact sizing */
        @media (pointer: coarse), (max-height: 500px) and (orientation: landscape) {
          .uqd-root {
            padding: 0.75rem 1rem !important;
          }
          .uqd-header {
            margin-bottom: 0.75rem !important;
            padding: 0.5rem 0.75rem !important;
            border-radius: 10px !important;
          }
          .uqd-progress {
            font-size: ${scaledFontSize('0.85rem')} !important;
          }
          .uqd-phase {
            font-size: ${scaledFontSize('0.75rem')} !important;
            min-width: auto !important;
            padding: 0.35rem 0.6rem !important;
          }
          .uqd-timer {
            font-size: ${scaledFontSize('1.6rem')} !important;
            padding: 0.4rem 0.7rem !important;
            min-width: 60px !important;
            border-radius: 8px !important;
          }
          .uqd-question-box {
            padding: 0.75rem 1rem !important;
            border-radius: 12px !important;
            margin-bottom: 0.75rem !important;
          }
          .uqd-question-text {
            font-size: ${scaledFontSize('1rem')} !important;
            margin-bottom: 0.75rem !important;
            line-height: 1.25 !important;
          }
          .uqd-choices {
            gap: 0.55rem !important;
          }
          .uqd-choices > div {
            padding: 0.5rem 0.75rem !important;
            border-radius: 10px !important;
            font-size: ${scaledFontSize('0.85rem')} !important;
            gap: 0.6rem !important;
            border-width: 2px !important;
          }
          .uqd-choices > div > div:first-child {
            width: 32px !important;
            height: 32px !important;
            font-size: ${scaledFontSize('0.9rem')} !important;
            border-width: 2px !important;
          }
          .uqd-choices > div > div:nth-child(2) {
            line-height: 1.2 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default UnifiedQuestionDisplay;
